import { useCallback, useEffect, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';

// Persisted theme preference. 'system' follows the OS prefers-color-scheme;
// 'light'/'dark' override it. The choice is stored in localStorage so it
// carries across the whole admin (set once at login, sticks everywhere).
const STORAGE_KEY = 'admin-theme-mode';
const VALID_MODES = ['system', 'light', 'dark'];
// Custom event so multiple hook instances (App + Login) stay in sync within
// the same tab; the 'storage' event only fires across tabs.
const CHANGE_EVENT = 'admin-theme-mode-change';

const readStored = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return VALID_MODES.includes(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
};

const useThemeMode = () => {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const [mode, setModeState] = useState(readStored);

  useEffect(() => {
    const sync = () => setModeState(readStored());
    window.addEventListener('storage', sync);
    window.addEventListener(CHANGE_EVENT, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(CHANGE_EVENT, sync);
    };
  }, []);

  const setMode = useCallback((next) => {
    if (!VALID_MODES.includes(next)) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage unavailable (private mode / blocked) — keep in-memory only.
    }
    setModeState(next);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  const resolvedMode = mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode;

  return { mode, resolvedMode, setMode };
};

export default useThemeMode;
